import * as acorn from 'acorn';
import * as acornWalk from 'acorn-walk';
import { parse as parseComment } from 'comment-parser';

/**
 * ASTノードからリテラル値を取得するヘルパー
 */
export function getLiteralValue(node) {
    if (node && node.type === 'Literal') {
        return node.value;
    }
    return null;
}

/**
 * MemberExpression (e.g., obj.prop.value) を文字列 'obj.prop.value' に変換するヘルパー
 */
export function resolveMemberExpressionToString(node) {
    if (!node) return null;
    if (node.type === 'Identifier') return node.name;
    if (node.type === 'MemberExpression' && !node.computed) {
        const objectPath = resolveMemberExpressionToString(node.object);
        const propertyPath = node.property.name;
        if (objectPath && propertyPath) {
            return `${objectPath}.${propertyPath}`;
        }
    }
    return null;
}

/**
 * ノードの直前にあるJSDocコメントを探すヘルパー
 * @param {object} node - Acorn ASTノード
 * @param {Array} comments - 抽出されたコメント配列
 * @param {string} sourceCode - 元のソースコード
 * @returns {string} JSDocコメント文字列 (/** ... \*\/) または空文字
 */
export function findPrecedingJsdoc(node, comments, sourceCode) {
    let precedingComment = null;

    for (let i = comments.length - 1; i >= 0; i--) {
        const comment = comments[i];
        if (comment.end < node.start) {
            const codeBetween = sourceCode.substring(comment.end, node.start);
            const trimmedCodeBetween = codeBetween.trim();

            // コメントとノードの間にあるのが「空白」または「宣言キーワードの羅列」のみかチェック
            const cleanCodeBetween = trimmedCodeBetween.replace(/(export|default|const|let|var|async|class|function)/g, '').trim();

            if (cleanCodeBetween === '') {
                precedingComment = comment;
                break;
            }
        }
    }

    if (precedingComment) {
        const text = precedingComment.text || precedingComment.value || '';
        // 既にJSDoc形式 (/** で始まる) ならそのまま、そうでなければJSDoc形式に整形して返す
        if (text.trim().startsWith('*')) {
            return `/*${text}*/`;
        }
        return `/** ${text} */`;
    }
    return '';
}

/**
 * JSDoc文字列から属性とイベントをパースする
 * @param {string} jsdocString - JSDocコメント文字列
 * @returns {{attributes: Array, events: Array}}
 */
export function parseJSDoc(jsdocString) {
    const attributes = [];
    const events = [];

    if (!jsdocString || jsdocString.trim() === '') {
        return { attributes, events };
    }

    let parsedBlocks;
    try {
        parsedBlocks = parseComment(jsdocString);
    } catch (e) {
        console.warn('Failed to parse JSDoc string with comment-parser:', jsdocString, e);
        return { attributes, events };
    }

    const firstBlock = parsedBlocks[0];
    if (!firstBlock || !firstBlock.tags) {
        return { attributes, events };
    }

    for (const tag of firstBlock.tags) {
        // @attribute または @property
        if (tag.tag === 'attribute' || tag.tag === 'property') {
            const name = tag.name;
            const description = tag.description || '';
            const type = tag.type || 'any';
            const cleanType = type.replace(/[{}]/g, '').trim();
            const { inputName, inputType, options } = deriveInputFromType(cleanType);

            attributes.push({
                name,
                inputName,
                inputType,
                options,
                comment: description
            });
        }
        // @fires または @event
        else if (tag.tag === 'fires' || tag.tag === 'event') {
            const cleanedComment = (tag.description || '').replace(/^\s*-\s*/, '').trim();
            events.push({
                name: tag.name,
                comment: cleanedComment || ''
            });
        }
    }

    return { attributes, events };
}

/**
 * JSDocの型文字列からUI入力タイプを推論する
 */
function deriveInputFromType(typeString) {
    let inputName = 'input';
    let inputType = 'text';
    let options = [];

    if (!typeString) return { inputName, inputType, options };

    let isArrayType = false;
    let processedType = typeString.trim();

    if (processedType.endsWith('[]')) {
        isArrayType = true;
        processedType = processedType.slice(0, -2).trim();
    }

    const parensMatch = processedType.match(/^\((.*)\)$/);
    if (parensMatch) {
        const innerContent = parensMatch[1].trim();
        const potentialOptions = innerContent.split(/\s*\|\s*/);
        const allAreLiterals = potentialOptions.every(opt =>
            (opt.startsWith("'") && opt.endsWith("'") && opt.length >= 2) ||
            (opt.startsWith('"') && opt.endsWith('"') && opt.length >= 2)
        );

        if (allAreLiterals) {
            options = potentialOptions.map(opt => opt.slice(1, -1));
            if (isArrayType) {
                inputName = 'multiselect';
                inputType = null;
            } else {
                inputName = 'select';
                inputType = null;
            }
        }
    }

    if (inputName === 'input' && options.length === 0) {
        switch (processedType.toLowerCase()) {
            case 'string': inputName = 'input'; inputType = 'text'; break;
            case 'number': inputName = 'input'; inputType = 'number'; break;
            case 'boolean': inputName = 'input'; inputType = 'checkbox'; break;
            default: inputName = 'input'; inputType = 'text'; break;
        }
    }

    return { inputName, inputType, options };
}

/**
 * スクリプトコードからすべての宣言情報（関数、クラス）を抽出する
 * @param {string} scriptCode - 解析対象コード
 * @returns {Object} 宣言名をキーとする情報オブジェクト { name: { comment, type, start } }
 */
export function extractDeclarationInfo(scriptCode) {
    if (!scriptCode) return {};

    let comments = [];
    let options = {
        ecmaVersion: 2022,
        onComment: (isBlock, text, start, end) => {
            if (isBlock && text.startsWith('*')) {
                // acornのtextは中身のみなので、ここでJSDocっぽく見えるように保持しておく必要はない
                // findPrecedingJsdocで再構築するため、ここでは生データを保存
                comments.push({ text: text, start, end });
            }
        },
        locations: true,
        ranges: true,
        sourceType: "module"
    };

    let ast;
    try {
        ast = acorn.parse(scriptCode, options);
    } catch (e) {
        return {};
    }

    const declarations = {};

    function getJsdocDescription(node) {
        const jsdocString = findPrecedingJsdoc(node, comments, scriptCode);
        if (!jsdocString) return null;
        try {
            const parsedBlocks = parseComment(jsdocString);
            if (parsedBlocks && parsedBlocks.length > 0) {
                // JSDocのdescriptionの最初の段落のみを使用
                return parsedBlocks[0].description.split('\n\n')[0].trim() || null;
            }
        } catch (e) { }

        // パース失敗時やJSDoc形式でない場合、最低限テキストを返す（/** や */ を除去して整形）
        return jsdocString.replace(/^\/\*+\s*/, '').replace(/\s*\*+\/$/, '').trim();
    }

    acornWalk.simple(ast, {
        FunctionDeclaration(node) {
            if (node.id) {
                declarations[node.id.name] = {
                    comment: getJsdocDescription(node),
                    type: 'function',
                    start: node.start
                };
            }
        },
        ClassDeclaration(node) {
            if (node.id) {
                declarations[node.id.name] = {
                    comment: getJsdocDescription(node),
                    type: 'class',
                    start: node.start
                };
            }
        },
        VariableDeclarator(node) {
            if (node.id.type === 'Identifier') {
                const isClassOrFuncExpression = node.init && (node.init.type === 'ClassExpression' || node.init.type === 'FunctionExpression');
                if (isClassOrFuncExpression) {
                    declarations[node.id.name] = {
                        comment: getJsdocDescription(node),
                        type: node.init.type === 'ClassExpression' ? 'class' : 'function',
                        start: node.start
                    };
                }
            }
        }
    });

    return declarations;
}

/**
 * スクリプトコードから「代表的な」1つの宣言情報を抽出する（ScriptItemSlice用）
 * コード内で最も最初に出現する関数またはクラスを対象とする
 * @param {string} scriptCode 
 */
export function deriveScriptMetadata(scriptCode) {
    // extractDeclarationInfo を再利用
    const allDeclarations = extractDeclarationInfo(scriptCode);

    // start位置でソートして最初の一つを取得
    const sorted = Object.entries(allDeclarations)
        .map(([name, info]) => ({ name, ...info }))
        .sort((a, b) => a.start - b.start);

    const first = sorted[0];

    if (!first) return null;

    return {
        name: first.name,
        type: first.type,
        comment: first.comment || ""
    };
}

/**
 * スクリプトコードからカスタム要素定義 (customElements.define) を抽出する
 * @param {string} scriptCode 
 */
export function findCustomElementDefinitions(scriptCode) {
    if (!scriptCode) return [];

    let comments = [];
    let ast;
    try {
        ast = acorn.parse(scriptCode, {
            ecmaVersion: 2022,
            sourceType: 'module',
            locations: true,
            onComment: (isBlock, text, start, end) => {
                if (isBlock && text.startsWith('*')) {
                    comments.push({ text: text, start, end });
                }
            }
        });
    } catch (e) {
        return [];
    }

    const definitions = [];
    const classInfoMap = new Map();
    const variableValueMap = new Map();

    acornWalk.simple(ast, {
        ClassDeclaration(node) {
            if (node.id) {
                const jsdoc = findPrecedingJsdoc(node, comments, scriptCode);
                classInfoMap.set(node.id.name, { node, jsdoc });
            }
        },
        VariableDeclarator(node) {
            if (node.id.type === 'Identifier') {
                if (node.init && node.init.type === 'ClassExpression') {
                    const jsdoc = findPrecedingJsdoc(node, comments, scriptCode);
                    classInfoMap.set(node.id.name, { node: node.init, jsdoc });
                } else if (node.init && node.init.type === 'Literal') {
                    variableValueMap.set(node.id.name, node.init.value);
                }
            }
        },
        AssignmentExpression(node) {
            if (node.right.type === 'ClassExpression') {
                const fullClassName = resolveMemberExpressionToString(node.left);
                if (fullClassName) {
                    const jsdoc = findPrecedingJsdoc(node, comments, scriptCode);
                    classInfoMap.set(fullClassName, { node: node.right, jsdoc });
                }
            }
        }
    });

    acornWalk.simple(ast, {
        CallExpression(node) {
            const calleeString = resolveMemberExpressionToString(node.callee);
            if (
                !(calleeString === 'customElements.define' || calleeString === 'window.customElements.define') ||
                node.arguments.length < 2
            ) {
                return;
            }

            const tagNameNode = node.arguments[0];
            const classNode = node.arguments[1];

            let tagName = getLiteralValue(tagNameNode);
            const className = resolveMemberExpressionToString(classNode);

            if (!tagName) {
                if (tagNameNode.type === 'Identifier') {
                    tagName = variableValueMap.get(tagNameNode.name);
                } else if (tagNameNode.type === 'MemberExpression') {
                    const exprStr = resolveMemberExpressionToString(tagNameNode);
                    const classPath = exprStr.substring(0, exprStr.lastIndexOf('.'));
                    const propName = exprStr.substring(exprStr.lastIndexOf('.') + 1);

                    const classInfo = classInfoMap.get(classPath);
                    if (classInfo && classInfo.node.body) {
                        for (const member of classInfo.node.body.body) {
                            if (member.static && member.key.name === propName) {
                                if (member.type === 'PropertyDefinition' && member.value && member.value.type === 'Literal') {
                                    tagName = member.value.value;
                                } else if (member.type === 'MethodDefinition' && member.kind === 'get') {
                                    const ret = member.value.body.body.find(n => n.type === 'ReturnStatement');
                                    if (ret && ret.argument && ret.argument.type === 'Literal') {
                                        tagName = ret.argument.value;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (tagName && className) {
                const info = classInfoMap.get(className);
                definitions.push({
                    tagName,
                    className,
                    jsdoc: info ? info.jsdoc : '',
                });
            }
        }
    });

    return definitions;
}

/**
 * Monaco Editor補完用の型定義を抽出する
 */
export function extractTypeDefinitions(code) {
    let comments = [];
    let options = {
        ecmaVersion: 2022,
        onComment: (block, text, start, end) => {
            if (block && text.startsWith('*')) {
                let commentText = code.substring(start, end);
                if (commentText.startsWith('/***')) {
                    commentText = '/**' + commentText.substring(3);
                }
                comments.push({ text: commentText, start, end });
            }
        },
        locations: true,
        ranges: true,
        sourceType: "module"
    };

    let ast = null;
    try {
        ast = acorn.parse(code, options);
    } catch (e) {
        return { functions: {}, variables: {}, classes: {} };
    }

    const allDeclarations = [];
    acornWalk.simple(ast, {
        FunctionDeclaration(node) { allDeclarations.push(node); },
        ClassDeclaration(node) { allDeclarations.push(node); },
        VariableDeclaration(node) { allDeclarations.push(node); }
    });

    allDeclarations.sort((a, b) => a.start - b.start);
    comments.sort((a, b) => a.start - b.start);

    const nodeToJsdocMap = new Map();
    let declIndex = 0;
    let commentIndex = 0;

    while (declIndex < allDeclarations.length && commentIndex < comments.length) {
        const declNode = allDeclarations[declIndex];
        const commentNode = comments[commentIndex];

        if (commentNode.end < declNode.start) {
            const codeBetween = code.substring(commentNode.end, declNode.start);
            if (codeBetween.trim() === '') {
                // findPrecedingJsdoc を使って整形した文字列をセットする
                // （comment.text は中身だけなので）
                let jsdoc = commentNode.text;
                if (jsdoc.trim().startsWith('*')) {
                    jsdoc = `/*${jsdoc}*/`;
                } else {
                    jsdoc = `/** ${jsdoc} */`;
                }
                nodeToJsdocMap.set(declNode, jsdoc);
            }
            commentIndex++;
        } else {
            declIndex++;
        }
    }

    const functions = {};
    const variables = {};
    const classes = {};

    function extractParamTypes(docCommentString) {
        const paramTypes = {};
        if (!docCommentString) return paramTypes;
        try {
            const parsedBlocks = parseComment(docCommentString);
            if (parsedBlocks && parsedBlocks.length > 0 && parsedBlocks[0].tags) {
                parsedBlocks[0].tags
                    .filter(tag => tag.tag === 'param' && tag.name)
                    .forEach(paramTag => {
                        const type = paramTag.type ? paramTag.type.replace(/[{}]/g, '').trim() : 'any';
                        paramTypes[paramTag.name] = type;
                    });
            }
        } catch (e) { }
        return paramTypes;
    }

    function extractReturnType(docCommentString) {
        try {
            const parsedBlocks = parseComment(docCommentString);
            if (parsedBlocks && parsedBlocks.length > 0 && parsedBlocks[0].tags) {
                const returnTag = parsedBlocks[0].tags.find(tag => tag.tag === 'returns' || tag.tag === 'return');
                if (returnTag && returnTag.type) {
                    return returnTag.type.replace(/[{}]/g, '').trim();
                }
            }
        } catch (e) { }
        return null;
    }

    function inferType(node) {
        if (!node) return 'any';
        switch (node.type) {
            case 'Literal': return typeof node.value;
            case 'Identifier': return 'any';
            case 'ArrayExpression': return 'any[]';
            case 'ObjectExpression': return 'object';
            default: return 'any';
        }
    }

    function generateFunctionSignature(node, docCommentString) {
        const paramTypes = extractParamTypes(docCommentString);
        const returnType = extractReturnType(docCommentString) || 'any';
        const params = node.params.map((param) => {
            const paramName = (param && param.name) ? param.name : 'arg';
            const jsdocType = paramTypes[paramName];
            return `${paramName}: ${jsdocType || 'any'}`;
        }).join(', ');
        const commentBlock = docCommentString ? `${docCommentString}\n` : '';
        const functionName = node.id ? node.id.name : '';
        return `${commentBlock}declare function ${functionName}(${params}): ${returnType};`;
    }

    function generateVariableSignature(declarator, declarationKind, docCommentString) {
        const inferredType = declarator.init ? inferType(declarator.init) : "any";
        const commentBlock = docCommentString ? `${docCommentString}\n` : '';
        return `${commentBlock}declare ${declarationKind} ${declarator.id.name}: ${inferredType};`;
    }

    function generateDetailedClassSignature(classNode, classDocComment, getPrecedingJsdoc) {
        const className = classNode.id.name;
        let members = '';
        if (classNode.body && classNode.body.body) {
            classNode.body.body.forEach(member => {
                const memberDocCommentString = ''; // クラスメンバのコメントは簡易実装のため空
                const memberCommentBlock = memberDocCommentString ? `    ${memberDocCommentString}\n` : '    ';
                if (member.type === 'MethodDefinition') {
                    const methodName = member.key.name;
                    const isStatic = member.static;
                    const returnType = methodName === 'constructor' ? '' : 'any';

                    if (member.kind === 'get' || member.kind === 'set') {
                        members += `${memberCommentBlock}${isStatic ? 'static ' : ''}${member.kind} ${methodName}(): ${returnType};\n`;
                    } else {
                        const params = member.value.params.map((param, index) => {
                            let paramName = 'arg' + index;
                            if (param.type === 'Identifier') paramName = param.name;
                            else if (param.type === 'AssignmentPattern' && param.left.type === 'Identifier') paramName = param.left.name;
                            return `${paramName}: any`;
                        }).join(', ');
                        if (methodName === 'constructor') {
                            members += `${memberCommentBlock}constructor(${params});\n`;
                        } else {
                            members += `${memberCommentBlock}${isStatic ? 'static ' : ''}${methodName}(${params}): ${returnType};\n`;
                        }
                    }
                } else if (member.type === 'PropertyDefinition') {
                    const propName = member.key.name;
                    const isStatic = member.static;
                    const propType = (member.value ? inferType(member.value) : 'any');
                    members += `${memberCommentBlock}${isStatic ? 'static ' : ''}${propName}: ${propType};\n`;
                }
            });
        }
        const extendsClause = classNode.superClass ? ` extends ${classNode.superClass.name}` : '';
        const classCommentBlock = classDocComment ? `${classDocComment}\n` : '';
        return `${classCommentBlock}declare class ${className}${extendsClause} {\n${members}}`;
    }

    for (const node of allDeclarations) {
        const docCommentString = nodeToJsdocMap.get(node) || '';
        if (node.type === 'FunctionDeclaration') {
            const functionSignature = generateFunctionSignature(node, docCommentString);
            functions[node.id.name] = { signature: functionSignature, docComment: docCommentString };
        } else if (node.type === 'ClassDeclaration') {
            const classSignature = generateDetailedClassSignature(node, docCommentString, () => '');
            if (node.id && node.id.name) {
                classes[node.id.name] = {
                    signature: classSignature,
                    docComment: docCommentString,
                };
            }
        } else if (node.type === 'VariableDeclaration') {
            node.declarations.forEach(declarator => {
                if (declarator.id && declarator.id.type === 'Identifier') {
                    const declarationKind = node.kind;
                    const variableSignature = generateVariableSignature(declarator, declarationKind, docCommentString);
                    variables[declarator.id.name] = {
                        signature: variableSignature,
                        initializer: code.substring(declarator.start, declarator.end),
                        typeFromJsdoc: null,
                        docComment: docCommentString,
                    };
                }
            });
        }
    }

    return { functions, variables, classes };
}